import { createFileRoute, Link } from "@tanstack/react-router";
import { AppShell } from "@/lovable-demo/components/app-shell";
import { Panel, RiskBadge, StatusChip, VerdictBadge } from "@/lovable-demo/components/bits";
import { C1000Chart, Sparkline } from "@/lovable-demo/components/charts";
import { recordsForProgram, rpnDelta, topFive } from "@/lovable-demo/lib/warranty/aggregate";
import { recordApDelta } from "@/lovable-demo/lib/warranty/action-priority";
import {
  currentStage,
  fmtCompactCurrency,
  fmtCurrency,
  fmtNumber,
  recordDelayCost,
  recordNpr,
  recordVerdict,
} from "@/lovable-demo/lib/warranty/calc";
import { useWarranty } from "@/lovable-demo/lib/warranty/store";

export const Route = createFileRoute("/demo/top5")({
  head: () => ({
    meta: [
      { title: "Top 5 Warranty Intelligence — Broadview" },
      {
        name: "description",
        content:
          "The five highest-risk warranty issues ranked by risk score, exposure and stage position, each linked to its governed Problem Record.",
      },
      { property: "og:title", content: "Top 5 Warranty Intelligence — Broadview" },
      {
        property: "og:description",
        content:
          "The five highest-risk warranty issues ranked by risk score, exposure and stage position, each linked to its governed Problem Record.",
      },
    ],
  }),
  component: Top5,
});

function Top5() {
  const { data, activeProgramId } = useWarranty();
  const records = recordsForProgram(data, activeProgramId);
  const five = topFive(records);

  return (
    <AppShell
      title="Top 5 Intelligence"
      subtitle="Risk-prioritised issues accepted into management review"
    >
      <div className="space-y-4">
        {five.map((r, i) => {
          const stage = currentStage(r.stages);
          const delta = rpnDelta(r);
          const ap = recordApDelta(r.pfmea);
          return (
            <Panel
              key={r.id}
              actions={
                <Link
                  to="/demo/problem-record/$id"
                  params={{ id: r.id }}
                  className="text-xs text-primary hover:underline"
                >
                  Open governed record
                </Link>
              }
              title={`Rank ${i + 1} · ${r.code}`}
              subtitle={r.title}
            >
              <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <RiskBadge risk={r.risk} score={r.riskScore} />
                    <StatusChip status={r.status} />
                    <VerdictBadge verdict={recordVerdict(r.financial)} />
                    <span className="num text-xs text-muted-foreground">
                      stage {stage.index}/11 — {stage.name}
                    </span>
                  </div>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/90">{r.description}</p>
                  <dl className="mt-4 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
                    {[
                      ["Claims", fmtNumber(r.claims)],
                      ["Exposure", fmtCompactCurrency(r.financial.exposure)],
                      ["Remaining exposure", fmtCompactCurrency(recordNpr(r))],
                      ["Delay / mo", fmtCompactCurrency(recordDelayCost(r))],
                      ["Highest AP", `${ap.before} → ${ap.after}`],
                      ["RPN (legacy)", `${delta.before} → ${delta.after}`],
                    ].map(([k, v]) => (
                      <div key={k}>
                        <dt className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
                          {k}
                        </dt>
                        <dd className="num text-sm font-semibold">{v}</dd>
                      </div>
                    ))}
                  </dl>
                  <p className="mt-4 text-xs text-muted-foreground">
                    Supplier {r.supplier} · plant {r.plant} · owner {r.owner} · champion{" "}
                    {r.champion}
                  </p>
                </div>
                <div>
                  <p className="text-[0.62rem] uppercase tracking-wider text-muted-foreground">
                    C/1000 for this failure mode
                  </p>
                  <div className="mt-1">
                    <C1000Chart data={r.c1000} height={140} />
                  </div>
                  <div className="mt-2 flex items-center gap-3">
                    <div className="h-8 flex-1">
                      <Sparkline data={r.imr} tone="2" />
                    </div>
                    <span className="num text-xs text-muted-foreground">
                      {fmtCurrency(r.financial.actualCost)} incurred
                    </span>
                  </div>
                </div>
              </div>
            </Panel>
          );
        })}
      </div>
    </AppShell>
  );
}
